import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CoreConfigService } from '@core/services/config.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { IUser } from 'app/interfaces/user.interface';
import { ApiRoutes } from 'app/routes/app.routes';
import { AuthService } from 'app/services/auth/auth.service';
import { SchoolService } from 'app/services/school/school.service';
import { AppStateManager } from 'app/state/app.state';
import { FileHandler } from 'app/utils/file-handler.utils';
import { Cipher, LocalStore } from 'app/utils/localstore.utils';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AccountSettingsComponent implements OnInit {
  currentScreen = {
    user: {
      active: true,
      onEnter: false,
    },
    school: {
      active: false,
      onEnter: false,
    },
  };

  // Public
  public coreConfig: any;
  public passwordTextType: boolean;
  public registerForm: UntypedFormGroup;
  public submitted = false;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param auth
   * @param school
   * @param router
   * @param formBuilder
   * @param fileUpload
   * @param state
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private auth: AuthService,
    private school: SchoolService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private fileUpload: FileHandler,
    private state: AppStateManager
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };

    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });
  }

  ngOnInit(): void {}

  imageObject = {
    profile_photo: '../../../../../assets/images/pages/blank-profile.webp',
    logo: '../../../../../assets/images/pages/no-logo.png',
  };

  selectedFile = '';

  newUser = this.formBuilder.group({
    user: this.formBuilder.group({
      address: ['', [Validators.required, Validators.minLength(3)]],
      phone_number: ['', [Validators.required, Validators.minLength(3)]],
    }),
    school: this.formBuilder.group({
      address: ['', [Validators.required, Validators.minLength(3)]],
      phone_number: ['', [Validators.required, Validators.minLength(3)]],
      logo: [''],
    }),
  });

  toggleScreens(screen: 'school' | 'user') {
    if (screen === 'user') {
      this.currentScreen.user = {
        active: false,
        onEnter: false,
      };
      this.currentScreen.school = {
        active: true,
        onEnter: true,
      };
    }
    if (screen === 'school') {
      this.currentScreen.user = {
        active: true,
        onEnter: true,
      };
      this.currentScreen.school = {
        active: false,
        onEnter: true,
      };
    }
  }

  /** **
   * @author Nsama David
   * @description method that returns AbstractControl for fom group belonging to particular group context
   * @param groupName string
   * @param controlName string
   * @returns AbstractControl AbstractControl
   */
  getFormControl(groupName: string, controlName: string): AbstractControl {
    return this.newUser.get(`${groupName}.${controlName}`);
  }

  createAccount() {
    this.upload();
    if (this.newUser.invalid) return;

    // const payload = this.newUser.value;
    // payload.school.type = this.getFormControl('school', 'type').value?.name;

    // this.auth.createAccount(payload).subscribe((userResponse) => {
    //   // set these keys here to help with the file upload
    //   LocalStore.setItem('token', userResponse.data.refresh_token);
    //   LocalStore.setItem('x_api_key', userResponse.data.x_api_key);
    //   this.authActions(userResponse);
    // });
  }

  upload() {
    // file upload
    const formData = new FormData();
    if (this.getFormControl('user', 'profile_photo')?.value) {
      formData.append(
        'profile_photo',
        this.getFormControl('user', 'profile_photo')?.value
      );
    }
    formData.append('logo', this.getFormControl('school', 'logo')?.value);
    this.fileUpload.manyFilesUpload(formData, 'school-logo').subscribe();
  }

  fileHandler(event: any, groupName: string) {
    const files = Array.from(event.target.files);
    this.getFormControl(groupName, event.target.name ?? 'logo')?.setValue(
      files[0] as any
    );
    this.selectedFile = this.fileUpload.single(files);

    if (event.target.name) {
      const reader = new FileReader();
      reader.onload = () => {
        this.imageObject[event.target.name] = reader.result as string;
      };
      reader.readAsDataURL(files[0] as any);
    }
  }

  authActions(response: any) {
    const responseData: IUser = response.data;
    LocalStore.setItem('token', responseData.refresh_token);
    LocalStore.setItem('x_api_key', responseData.x_api_key);
    LocalStore.setItem('user', responseData);
    this.state.setUserState(responseData);
    this.router.navigate([`/app/${ApiRoutes.dashboard.home}`], {
      queryParams: {
        [responseData?.userType ?? 'admin']: Cipher.encrypt(responseData?.id),
      },
    });
  }

  ngOnDestroy() {
    URL.revokeObjectURL(this.selectedFile);
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
